import type { SourcingEventDto } from "../../../autogen/bff-api";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";

export type JuryRankingDeparture = "REFUSED_BY_BINDING_JURY" | "NEEDS_REASON";

const contestOf = (event: SourcingEventDto) =>
  eventIsDesignContest(event) && event.eventContext.type === "Public" ? event.eventContext.designContest : null;

export const juryOnlyAdvises = (event: SourcingEventDto): boolean => contestOf(event)?.juryDecisionBinding === false;

// Mirrors SourcingEventService.withJuryRankingFollowed in the BFF.
export const juryRankingDeparture = (
  event: SourcingEventDto,
  winnerIds: readonly string[],
  rejectedIds: ReadonlySet<string> = new Set(),
): JuryRankingDeparture | null => {
  const contest = contestOf(event);
  if (!contest || winnerIds.length === 0) return null;
  const topRanked = (contest.juryReport?.rankedEntries ?? [])
    .map((ranked) => ranked.bidId)
    .filter((bidId) => !rejectedIds.has(bidId))
    .slice(0, winnerIds.length);
  const followsRanking = topRanked.length === winnerIds.length && topRanked.every((id) => winnerIds.includes(id));
  if (followsRanking) return null;
  return juryOnlyAdvises(event) ? "NEEDS_REASON" : "REFUSED_BY_BINDING_JURY";
};

export interface RejectedRankedEntry {
  bidId: string;
  place: number;
}

export const rejectedRankedAboveWinners = (
  event: SourcingEventDto,
  winnerIds: readonly string[],
  rejectedIds: ReadonlySet<string>,
): RejectedRankedEntry[] => {
  const contest = contestOf(event);
  if (!contest || juryOnlyAdvises(event)) return [];
  const ranked = (contest.juryReport?.rankedEntries ?? []).map((entry) => entry.bidId);
  const lowestWinnerPlace = Math.max(-1, ...winnerIds.map((id) => ranked.indexOf(id)));
  return ranked
    .slice(0, lowestWinnerPlace)
    .map((bidId, index) => ({ bidId, place: index + 1 }))
    .filter(({ bidId }) => rejectedIds.has(bidId));
};
